import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { RSVP_COLORS } from '../lib/rsvpPresentation';
import { uiLocale, monthShort, weekdaysShortMonFirst } from '../lib/dateFormat';
import type { Hunt, Team, RsvpStatus } from '../lib/types';
import { huntDisplayName, downedCount, observedCount } from '../lib/huntTitle';
import { compareInvitesNewestFirst } from '../lib/invitationOrder';
import { PageHeader } from './atoms';
import { Icon, type IconName } from './Icon';
import { LiveDot } from './LiveDot';
import { DraftsRail, PendingInvitesRail } from './HuntRails';
import { HuntStatsBar } from './HuntStatsBar';
import { WaSelect } from './WaSelect';
import { MonthYearPicker } from './MonthYearPicker';
import { JoinHuntModal } from './JoinHuntModal';
import { ConfirmModal } from './ConfirmModal';
import { useToast } from './Toast';
import { useHiddenHunts } from '../lib/useHiddenHunts';



/** Fixed box for one pin-and-count tally (18px pin + gap + up to 3 digits), so
 *  the downed/observed pin columns line up across every ended row. */
const PIN_TALLY_WIDTH = 46;

const isoOf = (dt: Date) =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;

export type HuntsView = "month" | "list";
/**
 * "pending" is not a hunt status — it is an upcoming hunt the viewer has not
 * answered yet. It earns a filter of its own because the stats bar counts it,
 * and a count nobody can open is just decoration.
 */
export type HuntsStatusFilter = "all" | "pending" | "upcoming" | "ongoing" | "ended" | "expired";

/** What the stats section can ask the page to show. */
export interface HuntsStatsActions {
  showPending: () => void;
  showUpcoming: () => void;
  showEnded: () => void;
}

export interface HuntsPageProps {
  hunts: Hunt[];
  teams: Team[];
  // groupId is the draft's team — required to reopen a draft, whose read is keyed
  // by group. Omitted when starting a fresh plan.
  onPlanHunt: (draftId?: string, groupId?: string) => void;
  onOpenHunt: (huntId: string) => void;
  /** Discard (delete) a draft. The Drafts rail confirms before calling this. */
  onDiscardDraft?: (hunt: Hunt) => void;
  /**
   * Edit a hunt's plan, from the list view's rows. The button shows only on
   * rows whose `hunt.canEditPlan` is true — the host resolves that per hunt,
   * since only it knows whether the viewer created the hunt or leads it.
   * Omitted by hosts that have nowhere to send the viewer.
   */
  onEditHunt?: (hunt: Hunt) => void;
  onMonthChange?: (year: number, month: number) => void;
  /**
   * The stats bar, injected because its numbers come from an API this library
   * cannot call. Pass a function to receive the actions that jump to each
   * list — a plain node still works for callers with nothing to wire.
   */
  statsSection?: ReactNode | ((actions: HuntsStatsActions) => ReactNode);
  /**
   * Filter and view are controlled when supplied, so a host can put them in the
   * URL and let Back undo a jump into a filtered list. Omit them and the page
   * keeps its own state, as it always has.
   */
  filter?: HuntsStatusFilter;
  view?: HuntsView;
  /**
   * Reports the complete next pair whenever either changes — never one half.
   * A stat tile changes both at once, and two separate callbacks in one tick
   * would have the second overwrite the first with a value it read before the
   * first landed.
   */
  onBrowseChange?: (next: { filter: HuntsStatusFilter; view: HuntsView }) => void;
  /** Plan a hunt pre-filled for a specific day (ISO `YYYY-MM-DD`). When given,
   *  the chosen-day panel shows a "Plan hunt" button for today/future days. */
  onPlanHuntForDate?: (iso: string) => void;
  /** Join a hunt by code. When given, a "Join hunt" action opens the code popup.
   *  Resolve 'joined' for a real join or 'viewed' for a read-only look at a
   *  hunt that already ended; reject (Error) to surface the message in the
   *  popup. */
  onJoinByCode?: (code: string) => Promise<'joined' | 'viewed'>;
  /** Validate the code format (e.g. NNNLLL) before enabling Join in the popup. */
  validateJoinCode?: (code: string) => boolean;
}

export function HuntsPage({ hunts: allHunts, teams, onPlanHunt, onOpenHunt, onDiscardDraft, onEditHunt, onMonthChange, statsSection, onPlanHuntForDate, onJoinByCode, validateJoinCode, filter: filterProp, view: viewProp, onBrowseChange }: HuntsPageProps) {
  const { t } = useTranslation('hunts');
  const { showToast } = useToast();
  // Device-local "removed from my list" set — hides an ENDED hunt for this user
  // on this device only, non-destructively (see useHiddenHunts). A planned or
  // running hunt is never filtered, so one the viewer takes part in stays
  // reachable, and ids stored by earlier versions for upcoming hunts stop
  // hiding them. Filtering here covers every view and reacts on the spot.
  const { hiddenIds, hide, unhide } = useHiddenHunts();
  const hunts = useMemo(
    () => allHunts.filter((h) => h.status !== "ended" || !hiddenIds.has(h.id)),
    [allHunts, hiddenIds],
  );
  // The hunt awaiting remove-confirmation, or null when the dialog is closed.
  const [removeTarget, setRemoveTarget] = useState<Hunt | null>(null);
  const confirmRemove = () => {
    if (!removeTarget) return;
    const { id } = removeTarget;
    hide(id);
    setRemoveTarget(null);
    showToast(t('remove.toast'), { label: t('remove.undo'), onAction: () => unhide(id) });
  };
  // Controlled when the host supplies a value, self-managed otherwise. The
  // internal state is still written in the controlled case so a host that
  // supplies only the callback (no value) keeps working.
  const [ownView, setOwnView] = useState<HuntsView>("month");
  const [ownFilter, setOwnFilter] = useState<HuntsStatusFilter>("all");
  const view = viewProp ?? ownView;
  const filter = filterProp ?? ownFilter;
  const browse = (next: { filter?: HuntsStatusFilter; view?: HuntsView }) => {
    const nextFilter = next.filter ?? filter;
    const nextView = next.view ?? view;
    setOwnFilter(nextFilter);
    setOwnView(nextView);
    onBrowseChange?.({ filter: nextFilter, view: nextView });
  };
  const setView = (next: HuntsView) => browse({ view: next });
  const setFilter = (next: HuntsStatusFilter) => browse({ filter: next });
  const [joinOpen, setJoinOpen] = useState(false);
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  // Auto-select today on entry so the day-detail panel is populated without a
  // manual "Today" click. HuntsPage re-mounts on navigation, so this snaps to
  // today each time the user reaches the calendar (fresh load or back-nav).
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const filtered = hunts.filter((h) => {
    if (h.status === "draft") return false;
    // Pending cuts across status: an upcoming hunt the viewer hasn't answered.
    if (filter === "pending" && !(h.status === "upcoming" && h.myRsvp === "pending")) return false;
    if (filter === "upcoming" && h.status !== "upcoming") return false;
    if (filter === "ongoing" && h.status !== "ongoing") return false;
    if (filter === "ended" && h.status !== "ended") return false;
    if (filter === "expired" && h.status !== "expired") return false;
    // Expired invitations are shown only when asked for. They are dead entries
    // — a hunt that never happened — so letting them into "All" would bury the
    // live ones under years of ignored invites.
    if (filter !== "expired" && h.status === "expired") return false;
    if (teamFilter !== "all" && h.teamId !== teamFilter) return false;
    return true;
  });

  const stats = {
    upcoming: hunts.filter((h) => h.status === "upcoming").length,
    ended:    hunts.filter((h) => h.status === "ended").length,
    drafts:   hunts.filter((h) => h.status === "draft").length,
    invites:  hunts.filter((h) => h.status === "upcoming" && h.myRsvp === "pending").length,
    expired:  hunts.filter((h) => h.status === "expired").length,
    harvest:  hunts.reduce((acc, h) => acc + (h.harvest?.reduce((s, x) => s + x.count, 0) ?? 0), 0),
  };

  const handleOpenInvite = (h: Hunt) => onOpenHunt(h.id);

  // A stat tile promises a list, so each action switches to the list view as
  // well — filtering the month calendar leaves the hunter looking at a grid.
  const showList = (next: HuntsStatusFilter) => browse({ filter: next, view: "list" });
  const statsActions: HuntsStatsActions = {
    showPending: () => showList("pending"),
    showUpcoming: () => showList("upcoming"),
    showEnded: () => showList("ended"),
  };

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        subtitle={t('page.subtitle')}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            {onJoinByCode && (
              <button className="wh-btn wh-btn-outline" onClick={() => setJoinOpen(true)}>
                <Icon name="plus" size={16} />
                {t('page.joinHunt')}
              </button>
            )}
            <button className="wh-btn wh-btn-primary" onClick={() => onPlanHunt()}>
              <Icon name="plus" size={16} />
              {t('page.planHunt')}
            </button>
          </div>
        }
      />
      {joinOpen && onJoinByCode && (
        <JoinHuntModal
          onJoin={onJoinByCode}
          validateFormat={validateJoinCode}
          onClose={() => setJoinOpen(false)}
        />
      )}

      {typeof statsSection === 'function' ? statsSection(statsActions) : statsSection ?? (
        <HuntStatsBar
          pendingInvitations={stats.invites}
          upcomingHunts={stats.upcoming}
          endedHunts={stats.ended}
          harvestToDate={stats.harvest}
          onViewInvitations={statsActions.showPending}
          onViewUpcoming={statsActions.showUpcoming}
          onViewEnded={statsActions.showEnded}
        />
      )}

      <div className="wh-card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <SegControl
            value={view}
            onChange={setView}
            options={[
              { v: "month", l: t('view.month'), icon: "calendar" },
              { v: "list",  l: t('view.list'),  icon: "dashboard" },
            ]}
          />
          <div style={{ width: 1, alignSelf: "stretch", background: COLORS.surfaceContainerHighest }} />
          <SegControl
            value={filter}
            onChange={setFilter}
            options={[
              { v: "all",      l: t('filters.all') },
              { v: "pending",  l: t('filters.pending') },
              { v: "upcoming", l: t('filters.upcoming') },
              { v: "ongoing",  l: t('filters.ongoing') },
              { v: "ended",    l: t('filters.ended') },
              // Only offered once there is something in it: for most accounts
              // this is empty, and a permanently-empty segment is noise.
              ...(stats.expired > 0
                ? [{ v: "expired" as const, l: t('filters.expired') }]
                : []),
            ]}
          />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, color: COLORS.outline, fontWeight: 600 }}>{t('teamFilter.label')}</label>
            <WaSelect
              ariaLabel={t('teamFilter.label')}
              width={220}
              value={teamFilter}
              onChange={setTeamFilter}
              options={[{ value: "all", label: t('teamFilter.allTeams') }, ...teams.map((tm) => ({ value: tm.id, label: tm.name }))]}
            />
          </div>
        </div>
      </div>

      {view === "month" ? (
        <MonthView
          cursor={cursor}
          setCursor={setCursor}
          today={today}
          hunts={filtered}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onOpenInvite={handleOpenInvite}
          onMonthChange={onMonthChange}
          onPlanHuntForDate={onPlanHuntForDate}
          rails={
            <>
              {stats.invites > 0 && <PendingInvitesRail hunts={hunts} compact onOpen={handleOpenInvite} />}
              {stats.drafts > 0 && <DraftsRail hunts={hunts} compact onContinue={(h) => onPlanHunt(h.id, h.teamId)} onDiscard={onDiscardDraft ?? (() => {})} />}
            </>
          }
        />
      ) : (
        <div className="wh-grid-2col-sm">
          <ListView hunts={filtered} newestInvitationsFirst={filter === "pending"} onOpenInvite={handleOpenInvite} onContinueDraft={(h) => onPlanHunt(h.id, h.teamId)} onRemoveHunt={setRemoveTarget} onEditHunt={onEditHunt} />
          {(stats.invites > 0 || stats.drafts > 0) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {stats.invites > 0 && <PendingInvitesRail hunts={hunts} onOpen={handleOpenInvite} />}
              {stats.drafts > 0 && <DraftsRail hunts={hunts} onContinue={(h) => onPlanHunt(h.id, h.teamId)} onDiscard={onDiscardDraft ?? (() => {})} />}
            </div>
          )}
        </div>
      )}

      {removeTarget && (
        <ConfirmModal
          title={t('remove.confirmTitle')}
          description={t('remove.confirmBody')}
          confirmLabel={t('remove.confirm')}
          cancelLabel={t('remove.cancel')}
          tone="primary"
          onConfirm={confirmRemove}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
    </div>
  );
}

function HuntStat({
  icon,
  label,
  value,
  tone,
  suffix,
}: {
  icon: IconName;
  label: string;
  value: number;
  tone: string;
  suffix?: string;
}) {
  return (
    <div className="wh-card" style={{ padding: 18, display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: tone + "1a",
          color: tone,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name={icon} size={22} />
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 700,
          }}
        >
          {label}
        </div>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 28, fontWeight: 500, lineHeight: 1.1 }}>
          {value}
          {suffix && (
            <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.outline, marginLeft: 6 }}>
              {suffix}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SegControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { v: T; l: string; icon?: IconName }[];
}) {
  return (
    <div style={{ display: "flex", gap: 2, background: COLORS.surface, padding: 3, borderRadius: 8 }}>
      {options.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            border: "none",
            padding: "7px 12px",
            borderRadius: 6,
            background: value === o.v ? COLORS.surfaceContainerLowest : "transparent",
            boxShadow: value === o.v ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
            fontSize: 13,
            fontWeight: 600,
            color: value === o.v ? COLORS.primary : COLORS.outline,
          }}
        >
          {o.icon && <Icon name={o.icon} size={14} />}
          {o.l}
        </button>
      ))}
    </div>
  );
}

function MonthView({
  cursor,
  setCursor,
  today,
  hunts,
  selectedDate,
  setSelectedDate,
  onOpenInvite,
  onMonthChange,
  onPlanHuntForDate,
  rails,
}: {
  cursor: Date;
  setCursor: (d: Date) => void;
  today: Date;
  hunts: Hunt[];
  selectedDate: Date | null;
  setSelectedDate: (d: Date | null) => void;
  onOpenInvite: (h: Hunt) => void;
  onMonthChange?: (year: number, month: number) => void;
  onPlanHuntForDate?: (iso: string) => void;
  rails: ReactNode;
}) {
  const { t, i18n } = useTranslation('hunts');
  const dowLabels = weekdaysShortMonFirst(uiLocale(i18n.language));
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const firstDow = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysPrev = new Date(year, month, 0).getDate();

  const cells: { day: number; inMonth: boolean; dateObj: Date }[] = [];
  for (let i = 0; i < firstDow; i++) {
    const d = daysPrev - firstDow + 1 + i;
    cells.push({ day: d, inMonth: false, dateObj: new Date(year, month - 1, d) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, inMonth: true, dateObj: new Date(year, month, d) });
  }
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const next = cells.length - (firstDow + daysInMonth) + 1;
    cells.push({ day: next, inMonth: false, dateObj: new Date(year, month + 1, next) });
    if (cells.length >= 42) break;
  }

  const huntsByDate = useMemo(() => {
    const m: Record<string, Hunt[]> = {};
    const todayKey = isoOf(today);
    hunts.forEach((h) => {
      // An ongoing hunt is happening NOW, so it belongs on today's cell. Keying it
      // on its start date hid a long-running hunt in a past month: one that began
      // months ago was invisible on the current month's calendar (even under the
      // Ongoing filter), though the List view showed it.
      const key = h.status === "ongoing" ? todayKey : h.date;
      (m[key] = m[key] || []).push(h);
    });
    return m;
  }, [hunts, today]);

  const selectedHunts = selectedDate ? huntsByDate[isoOf(selectedDate)] || [] : [];

  return (
    <div className="wh-grid-2col">
      <div className="wh-card" style={{ overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "16px 20px",
            borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          }}
        >
          <MonthYearPicker
            year={year}
            month={month}
            onPick={(y, m) => {
              // Jump the month cursor only — like the ‹/› arrows. selectedDate
              // is untouched, so this doesn't re-trigger the today-default
              // (which only applies on entry).
              const d = new Date(y, m, 1);
              setCursor(d);
              onMonthChange?.(d.getFullYear(), d.getMonth() + 1);
            }}
          />
          <button
            className="wh-btn"
            onClick={() => {
              const d = new Date();
              d.setDate(1);
              d.setHours(0, 0, 0, 0);
              setCursor(d);
              onMonthChange?.(d.getFullYear(), d.getMonth() + 1);
            }}
            style={{
              background: "transparent",
              border: `1px solid `,
              color: COLORS.onSurface,
              height: 36,
              padding: "0 14px",
            }}
          >
            {t('calendar.today')}
          </button>
          <button
            className="wh-btn-icon"
            onClick={() => {
              const d = new Date(year, month - 1, 1);
              setCursor(d);
              onMonthChange?.(d.getFullYear(), d.getMonth() + 1);
            }}
            aria-label={t('calendar.previousMonth')}
            style={{ transform: "rotate(90deg)" }}
          >
            <Icon name="chevron-down" size={16} />
          </button>
          <button
            className="wh-btn-icon"
            onClick={() => {
              const d = new Date(year, month + 1, 1);
              setCursor(d);
              onMonthChange?.(d.getFullYear(), d.getMonth() + 1);
            }}
            aria-label={t('calendar.nextMonth')}
            style={{ transform: "rotate(-90deg)" }}
          >
            <Icon name="chevron-down" size={16} />
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          }}
        >
          {dowLabels.map((d, i) => (
            <div
              key={i}
              style={{
                padding: "10px 12px",
                fontSize: 11,
                fontWeight: 700,
                color: COLORS.outline,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {d}
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridAutoRows: "minmax(110px, auto)" }}>
          {cells.map((c, i) => {
            const iso = isoOf(c.dateObj);
            // Ongoing first: an ongoing hunt now lands on today's cell (below), so
            // on a busy today it must not be the one hidden behind the two-pill
            // "+N" overflow — a live hunt is the most important thing to surface.
            const dayHunts = [...(huntsByDate[iso] || [])].sort(
              (a, b) => (a.status === "ongoing" ? 0 : 1) - (b.status === "ongoing" ? 0 : 1),
            );
            const isToday = c.dateObj.getTime() === today.getTime();
            const isPast = c.dateObj < today;
            const isSelected = selectedDate ? isoOf(selectedDate) === iso : false;
            return (
              <button
                key={i}
                data-iso={iso}
                onClick={() => setSelectedDate(new Date(c.dateObj))}
                style={{
                  textAlign: "left",
                  position: "relative",
                  // Pin the content to the cell's top-left: buttons vertically
                  // centre their label by default, which made the day number
                  // float mid-cell on days without hunts.
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  border: "none",
                  borderRight: (i + 1) % 7 === 0 ? "none" : `1px solid ${COLORS.surfaceContainerHighest}`,
                  borderBottom: i >= 35 ? "none" : `1px solid ${COLORS.surfaceContainerHighest}`,
                  padding: "8px 8px 6px",
                  background: isSelected ? COLORS.primaryOpacity8 : c.inMonth ? COLORS.surfaceContainerLowest : COLORS.surface,
                  // Clear selected state, distinct from today's filled date badge:
                  // an inset primary ring around the whole cell.
                  boxShadow: isSelected ? `inset 0 0 0 2px ${COLORS.primary}` : undefined,
                  overflow: "hidden",
                  opacity: c.inMonth ? 1 : 0.55,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: isToday ? 700 : 600,
                      background: isToday ? COLORS.primary : "transparent",
                      color: isToday ? COLORS.onPrimary : isPast && c.inMonth ? COLORS.outline : COLORS.onSurface,
                    }}
                  >
                    {c.day}
                  </div>
                  {dayHunts.length > 2 && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        color: COLORS.outline,
                        fontWeight: 700,
                      }}
                    >
                      +{dayHunts.length - 2}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {dayHunts.slice(0, 2).map((h) => {
                    const ongoing = h.status === "ongoing";
                    const ended = h.status === "ended";
                    const pillBg = ongoing ? "rgba(255,104,31,0.12)" : ended ? COLORS.tertiaryContainer : COLORS.secondaryContainer;
                    const pillFg = ongoing ? COLORS.onTertiaryFixedVariant : ended ? COLORS.onTertiaryContainer : COLORS.onSecondaryContainer;
                    return (
                      <div
                        key={h.id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); onOpenInvite(h); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onOpenInvite(h); } }}
                        // A month cell is narrow and hunt names are longer than
                        // a species name was, so most pills ellipsize — hover
                        // reaches the full title.
                        title={`${h.start} ${huntDisplayName(h)}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "3px 6px",
                          borderRadius: 4,
                          background: pillBg,
                          fontSize: 11,
                          fontWeight: ongoing ? 700 : 600,
                          color: pillFg,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          width: "100%",
                          cursor: "pointer",
                        }}
                      >
                        {ongoing ? (
                          <LiveDot size={6} />
                        ) : (
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 999,
                              background: pillFg,
                              flexShrink: 0,
                              opacity: 0.5,
                            }}
                          />
                        )}
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          <span style={{ opacity: ongoing ? 1 : 0.7, marginRight: 3 }}>{h.start}</span>
                          {/* The hunt's own name, or its team when unnamed —
                              never the species (CLUB-582). */}
                          {huntDisplayName(h)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <DayDetail date={selectedDate} hunts={selectedHunts} onOpenInvite={onOpenInvite} onPlanHuntForDate={onPlanHuntForDate} />
        {rails}
      </div>
    </div>
  );
}

function DayDetail({
  date,
  hunts,
  onOpenInvite,
  onPlanHuntForDate,
}: {
  date: Date | null;
  hunts: Hunt[];
  onOpenInvite: (h: Hunt) => void;
  onPlanHuntForDate?: (iso: string) => void;
}) {
  const { t, i18n } = useTranslation('hunts');
  if (!date) {
    return (
      <div className="wh-card" style={{ padding: 24, position: "sticky", top: 16 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            margin: "0 auto 14px",
            background: COLORS.primaryContainer,
            color: COLORS.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="calendar" size={26} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, textAlign: "center", marginBottom: 4 }}>{t('dayDetail.selectDay')}</div>
        <div style={{ fontSize: 13, color: COLORS.outline, textAlign: "center" }}>
          {t('dayDetail.selectDayHint')}
        </div>
      </div>
    );
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPast = date < today;
  const label = date.toLocaleDateString(uiLocale(i18n.language), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  // Opt-in "Plan hunt" CTA for the chosen day. Hidden on past days — you can't
  // plan a hunt in the past. Clicking pre-fills creation with the selected day.
  const planButton = onPlanHuntForDate && !isPast ? (
    <button
      type="button"
      data-testid="day-plan-hunt"
      className="wh-btn"
      onClick={() => onPlanHuntForDate(isoOf(date))}
      style={{
        width: "100%",
        marginTop: hunts.length > 0 ? 12 : 0,
        height: 36,
        background: "transparent",
        border: `1px solid `,
        color: COLORS.onSurface,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
      }}
    >
      <Icon name="plus" size={14} />
      {t('dayDetail.planHunt')}
    </button>
  ) : null;
  return (
    <div className="wh-card" data-testid="day-detail" style={{ position: "sticky", top: 16, overflow: "hidden" }}>
      {/* Tinted header band — anchors the chosen day so the panel reads as the
          focal card without leaning on elevation. */}
      <div style={{ background: COLORS.primaryContainer, padding: "14px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.onPrimaryFixedVariant,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {isPast ? t('dayDetail.badgeEnded') : date.getTime() === today.getTime() ? t('dayDetail.badgeToday') : t('dayDetail.badgeUpcoming')}
          </span>
        </div>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 20, fontWeight: 500, color: COLORS.onPrimaryContainer }}>
          {label}
        </div>
      </div>
      <div style={{ padding: 20 }}>
        {hunts.length === 0 ? (
          planButton ?? (
            <div style={{ fontSize: 13, color: COLORS.outline, padding: "20px 0", textAlign: "center" }}>
              {t('dayDetail.noHunts')}
            </div>
          )
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {hunts.map((h) => (
              <HuntCardCompact key={h.id} hunt={h} onOpenInvite={onOpenInvite} />
            ))}
            {planButton}
          </div>
        )}
      </div>
    </div>
  );
}

function HuntCardCompact({ hunt, onOpenInvite }: { hunt: Hunt; onOpenInvite: (h: Hunt) => void }) {
  const { t } = useTranslation('hunts');
  const ongoing = hunt.status === "ongoing";
  const ended = hunt.status === "ended";
  // The card leads with the hunt's own name, falling back to the team's — no
  // species chip (CLUB-582). When a name IS shown the team would otherwise
  // vanish from the card, so the meta row picks it up.
  const title = huntDisplayName(hunt);
  const showTeamInMeta = title !== hunt.team;
  const harvestCount = hunt.harvest?.reduce((s, x) => s + x.count, 0) ?? 0;
  const rsvp = hunt.myRsvp;
  const rsvpStyle = rsvpChipStyle(rsvp);
  // CLUB-272 — a hunt from the user's team that they are not part of (not invited).
  const notInvited = hunt.isParticipant === false;

  return (
    <div
      data-testid="day-hunt-card"
      style={{
        padding: 12,
        borderRadius: 8,
        background: COLORS.surface,
        border: `1px solid ${COLORS.surfaceContainerHighest}`,
        // Status, not species, now drives the accent (the quarry chip that set
        // the colour is gone).
        borderLeft: `3px solid ${ongoing ? COLORS.tertiary : ended ? COLORS.onTertiaryFixedVariant : COLORS.secondary}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
        {ongoing ? (
          <Chip background="rgba(255,104,31,0.12)" color={COLORS.onTertiaryFixedVariant}>
            <LiveDot size={7} />
            {t('status.ongoing')}
          </Chip>
        ) : ended ? (
          <Chip background={COLORS.surfaceContainerHighest} color={COLORS.onSurfaceVariant}>{t('status.ended')}</Chip>
        ) : rsvpStyle ? (
          <Chip background={rsvpStyle.bg} color={rsvpStyle.fg}>{t(rsvpStyle.labelKey)}</Chip>
        ) : (
          <Chip background="rgba(32,80,43,0.1)" color={COLORS.primary}>{t('status.upcoming')}</Chip>
        )}
        {notInvited && (
          <Chip background={COLORS.surfaceContainerHighest} color={COLORS.onSurfaceVariant}>{t('card.notInvited')}</Chip>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: COLORS.outline }}>
          {hunt.start}–{hunt.end}
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: COLORS.outline, marginBottom: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {showTeamInMeta && <span>{hunt.team}</span>}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Icon name="group" size={11} /> {t('card.participants', { count: hunt.participants })}
        </span>
      </div>
      {ended && harvestCount > 0 && hunt.harvest && (
        <div
          style={{
            fontSize: 12,
            padding: "6px 8px",
            background: COLORS.surfaceContainerLowest,
            borderRadius: 4,
            border: `1px solid ${COLORS.surfaceContainerHighest}`,
          }}
        >
          <b>{t('card.harvest')}</b> {hunt.harvest.map((x) => `${x.count} × ${x.species.toLowerCase()}`).join(", ")}
        </div>
      )}
      {notInvited ? (
        // A team hunt the viewer isn't part of: read-only. The card already
        // conveys team, status, time and participant count; the full detail
        // (map/drives/stands) requires joining (CLUB-171), and the detail
        // endpoint deliberately 403/404s a non-invitee — so offer no action
        // that would lead there.
        <div style={{ marginTop: 8, fontSize: 12, color: COLORS.outline, display: "flex", alignItems: "center", gap: 4 }}>
          <Icon name="group" size={12} /> {t('card.readOnly')}
        </div>
      ) : (
      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        {ongoing ? (
          <button
            className="wh-btn"
            style={{
              flex: 1,
              padding: "6px 10px",
              fontSize: 12,
              height: 32,
              background: "transparent",
              border: `1px solid `,
              color: COLORS.onSurface,
            }}
            onClick={() => onOpenInvite(hunt)}
          >
            {t('card.viewHunt')}
          </button>
        ) : ended ? (
          <button
            className="wh-btn"
            style={{
              flex: 1,
              padding: "6px 10px",
              fontSize: 12,
              height: 32,
              background: "transparent",
              border: `1px solid `,
              color: COLORS.onSurface,
            }}
            onClick={() => onOpenInvite(hunt)}
          >
            {t('card.review')}
          </button>
        ) : rsvp === "pending" ? (
          <button
            className="wh-btn wh-btn-primary"
            style={{ flex: 1, padding: "6px 10px", fontSize: 12, height: 32 }}
            onClick={() => onOpenInvite(hunt)}
          >
            {t('card.readAndRsvp')}
          </button>
        ) : rsvp ? (
          <button
            className="wh-btn"
            style={{
              flex: 1,
              padding: "6px 10px",
              fontSize: 12,
              height: 32,
              background: "transparent",
              border: `1px solid `,
              color: COLORS.onSurface,
            }}
            onClick={() => onOpenInvite(hunt)}
          >
            <Icon name="edit" size={12} />
            {t('card.viewChange')}
          </button>
        ) : (
          <button
            className="wh-btn"
            style={{
              flex: 1,
              padding: "6px 10px",
              fontSize: 12,
              height: 32,
              background: "transparent",
              border: `1px solid `,
              color: COLORS.onSurface,
            }}
            onClick={() => onOpenInvite(hunt)}
          >
            {t('card.viewPlanned')}
          </button>
        )}
        <button className="wh-btn-icon" aria-label={t('card.more')}>
          <Icon name="more" size={14} />
        </button>
      </div>
      )}
    </div>
  );
}

function rsvpChipStyle(rsvp?: RsvpStatus) {
  switch (rsvp) {
    case "pending":  return { ...RSVP_COLORS.pending,  labelKey: "rsvpChip.pending" };
    case "accepted": return { ...RSVP_COLORS.accepted, labelKey: "rsvpChip.accepted" };
    case "declined": return { ...RSVP_COLORS.declined, labelKey: "rsvpChip.declined" };
    default:         return null;
  }
}

function Chip({
  background,
  color,
  children,
}: {
  background: string;
  color: string;
  children: ReactNode;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 22,
        padding: "0 8px",
        borderRadius: 999,
        background,
        color,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

function ListView({
  hunts,
  newestInvitationsFirst = false,
  onOpenInvite,
  onContinueDraft,
  onRemoveHunt,
  onEditHunt,
}: {
  hunts: Hunt[];
  /** The list holds only pending invitations (the Pending filter), which are
   *  ordered like the invites rail instead of by date. */
  newestInvitationsFirst?: boolean;
  onOpenInvite: (h: Hunt) => void;
  onContinueDraft: (h: Hunt) => void;
  /** Request removing an ended hunt from the viewer's own list (opens the
   *  confirm). Offered in the Ended section only. */
  onRemoveHunt?: (h: Hunt) => void;
  /** Edit a hunt's plan. Offered only on rows the viewer may edit. */
  onEditHunt?: (h: Hunt) => void;
}) {
  const { t } = useTranslation('hunts');
  const sorted = [...hunts].sort((a, b) => a.date.localeCompare(b.date));
  const ongoing = sorted.filter((h) => h.status === "ongoing");
  const upcoming = sorted.filter((h) => h.status === "upcoming");
  if (newestInvitationsFirst) upcoming.sort(compareInvitesNewestFirst);
  const planned = [...ongoing, ...upcoming];
  const ended = sorted.filter((h) => h.status === "ended").reverse();

  if (planned.length === 0 && ended.length === 0) {
    return (
      <div className="wh-card" style={{ padding: 32, textAlign: "center" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            margin: "0 auto 14px",
            background: COLORS.primaryContainer,
            color: COLORS.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="hunts" size={26} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('list.emptyTitle')}</div>
        <div style={{ fontSize: 13, color: COLORS.outline }}>{t('list.emptyHint')}</div>
      </div>
    );
  }

  return (
    // Tagged so tests can scope to the list itself: the pending-invites rail
    // beside it names its hunts the same way now (CLUB-582), so an unscoped
    // text query matches the same hunt twice.
    <div data-testid="hunt-list" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {planned.length > 0 && (
        <ListSection title={t('list.sectionUpcoming')} hunts={planned} onOpenInvite={onOpenInvite} onContinueDraft={onContinueDraft} onEditHunt={onEditHunt} />
      )}
      {ended.length > 0 && (
        <ListSection title={t('list.sectionEnded')} hunts={ended} onOpenInvite={onOpenInvite} onContinueDraft={onContinueDraft} onRemoveHunt={onRemoveHunt} onEditHunt={onEditHunt} />
      )}
    </div>
  );
}

function ListSection({
  title,
  hunts,
  onOpenInvite,
  onContinueDraft,
  onRemoveHunt,
  onEditHunt,
}: {
  title: string;
  hunts: Hunt[];
  onOpenInvite: (h: Hunt) => void;
  onContinueDraft: (h: Hunt) => void;
  onRemoveHunt?: (h: Hunt) => void;
  onEditHunt?: (h: Hunt) => void;
}) {
  const { t } = useTranslation('hunts');
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 18, fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 13, color: COLORS.outline }}>
          {t('list.count', { count: hunts.length })}
        </div>
      </div>
      <div className="wh-card" style={{ overflow: "hidden" }}>
        {hunts.map((h, i) => (
          <HuntListRow
            key={h.id}
            hunt={h}
            last={i === hunts.length - 1}
            onOpenInvite={onOpenInvite}
            onContinueDraft={onContinueDraft}
            onRemoveHunt={onRemoveHunt}
            onEditHunt={onEditHunt}
          />
        ))}
      </div>
    </div>
  );
}

export function HuntListRow({
  hunt,
  last,
  onOpenInvite,
  onContinueDraft,
  onRemoveHunt,
  onEditHunt,
}: {
  hunt: Hunt;
  last: boolean;
  onOpenInvite: (h: Hunt) => void;
  onContinueDraft: (h: Hunt) => void;
  /** When given, the row shows a secondary overflow menu to remove the hunt
   *  from the viewer's own list. Absent for hosts that don't wire it. */
  onRemoveHunt?: (h: Hunt) => void;
  /** Edit this hunt's plan. The button appears only when the host wires this
   *  AND `hunt.canEditPlan` says the viewer may — see the Hunt type. */
  onEditHunt?: (h: Hunt) => void;
}) {
  const { t, i18n } = useTranslation('hunts');
  const locale = uiLocale(i18n.language);
  const ongoing = hunt.status === "ongoing";
  const ended = hunt.status === "ended";
  const draft = hunt.status === "draft";
  // CLUB-272 — a hunt from the viewer's team they aren't part of: read-only, no
  // navigation (the detail endpoint 403/404s a non-invitee; full detail needs
  // joining — CLUB-171).
  const teamHuntReadOnly = hunt.isParticipant === false;
  // Whether this row offers "Edit plan" in its overflow menu. Needs both a
  // handler and the host's per-hunt verdict (`canEditPlan`), and only applies
  // while the hunt is still an editable plan.
  const canEditPlanHere =
    !!onEditHunt &&
    !!hunt.canEditPlan &&
    hunt.status === "upcoming" &&
    !teamHuntReadOnly;
  const statusBorder = ongoing
    ? COLORS.tertiary
    : draft
      ? COLORS.tertiary
      : ended
        ? COLORS.onTertiaryFixedVariant
        : COLORS.secondary;
  const rsvp = hunt.myRsvp;
  const [y, m, d] = hunt.date.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.toLocaleDateString(locale, { weekday: "short" });
  const rsvpStyle = rsvpChipStyle(rsvp);
  // Name leads the row; team moves to the subtitle. (location holds the hunt name.)
  const name = hunt.location || `${hunt.quarry} hunt`;
  // Start date is mandatory; end date shows only when present and on a later day.
  const fmtShort = (iso: string) => {
    const [yy, mm, dd] = iso.split("-").map(Number);
    return `${dd} ${monthShort(locale, mm - 1)}`;
  };
  const dateDisplay =
    hunt.endDate && hunt.endDate !== hunt.date
      ? `${fmtShort(hunt.date)} – ${fmtShort(hunt.endDate)}`
      : fmtShort(hunt.date);
  const participantsLabel = t('card.participants', { count: hunt.participants });
  // Only an ended hunt whose feed actually carries outcome data gets a tally.
  // wehunt-web's hunt feeds populate neither `harvest` nor `observed` today, and
  // rendering the counts unconditionally would print a confident
  // "0 downed / 0 observed" on every ended hunt there — worse than silence,
  // because it reads as a measured result rather than missing data.
  const hasOutcome = hunt.harvest !== undefined || hunt.observed !== undefined;

  return (
    <div
      className="wh-hunt-row-grid"
      role={teamHuntReadOnly ? undefined : "button"}
      tabIndex={teamHuntReadOnly ? undefined : 0}
      onClick={teamHuntReadOnly ? undefined : () => draft ? onContinueDraft(hunt) : onOpenInvite(hunt)}
      onKeyDown={teamHuntReadOnly ? undefined : (e) => e.key === "Enter" && (draft ? onContinueDraft(hunt) : onOpenInvite(hunt))}
      style={{
        padding: "9px 16px",
        borderBottom: last ? "none" : `1px solid ${COLORS.surfaceContainerHighest}`,
        borderLeft: `3px solid ${statusBorder}`,
        cursor: teamHuntReadOnly ? "default" : "pointer",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 11, color: COLORS.outline, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>
          {dow}
        </div>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 22, fontWeight: 500, lineHeight: 1 }}>{d}</div>
        <div style={{ fontSize: 11, color: COLORS.outline, textTransform: "uppercase", fontWeight: 600 }}>
          {monthShort(locale, m - 1)}
        </div>
      </div>
      <div style={{ minWidth: 0 }}>
        {/* Name leads, with the status badge alongside. No huntable-games pill here. */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </span>
          {ongoing ? (
            <Chip background="rgba(255,104,31,0.12)" color={COLORS.onTertiaryFixedVariant}>
              <LiveDot size={7} />
              {t('status.ongoing')}
            </Chip>
          ) : draft ? (
            <Chip background="rgba(255,104,31,0.15)" color="#a03c16">
              {t('status.draft')}{hunt.draftStep ? ` · ${hunt.draftStep}` : ""}
            </Chip>
          ) : ended ? (
            <Chip background={COLORS.surfaceContainerHighest} color={COLORS.onSurfaceVariant}>{t('status.ended')}</Chip>
          ) : rsvpStyle ? (
            <Chip background={rsvpStyle.bg} color={rsvpStyle.fg}>{t(rsvpStyle.labelKey)}</Chip>
          ) : (
            <Chip background="rgba(32,80,43,0.1)" color={COLORS.primary}>{t('status.upcoming')}</Chip>
          )}
          {teamHuntReadOnly && (
            <Chip background={COLORS.surfaceContainerHighest} color={COLORS.onSurfaceVariant}>{t('list.teamHunt')}</Chip>
          )}
        </div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {hunt.team} · {dateDisplay} · {hunt.start}–{hunt.end} · {participantsLabel}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
        {teamHuntReadOnly ? (
          <span style={{ fontSize: 12, color: COLORS.outline }}>{t('list.readOnly')}</span>
        ) : ended ? (
          !hasOutcome ? null : (
          // What the hunt actually produced: head downed and head observed,
          // shown as the map pins that recorded them — the same pin-plus-count
          // pairing the hunt report uses for its per-hunter tallies. Replaces
          // the huntable-games list that used to sit here: the plan's species
          // say nothing about the outcome, and a six-species list did not fit
          // (CLUB-582).
          //
          // The pins are decorative (alt=""); each count carries the phrase on
          // the group instead, so a screen reader hears "2 downed" rather than
          // a bare "2". Both assets ship in wehunt-web's and the prototype's
          // public/assets — @wehunt/ui holds none of its own.
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
            {([
              ["downed", "/assets/map_pin_downed.svg", downedCount(hunt)],
              ["observed", "/assets/map_pin_observed.svg", observedCount(hunt)],
            ] as const).map(([key, src, count]) => {
              const label = t(`list.${key}`, { count });
              return (
                <span
                  key={key}
                  role="img"
                  aria-label={label}
                  title={label}
                  // Fixed-width so both pins land on the same x down the whole
                  // list. Sized to the group, not the content: letting the box
                  // shrink-wrap made a "21" row push its pins left of a "3"
                  // row's, so the pin column visibly zig-zagged.
                  style={{ display: "flex", alignItems: "center", gap: 4, width: PIN_TALLY_WIDTH, fontSize: 13, fontWeight: 700 }}
                >
                  <img src={src} alt="" width={18} height={19} style={{ display: "block", flexShrink: 0 }} />
                  {count}
                </span>
              );
            })}
          </div>
          )
        ) : draft ? (
          <button
            className="wh-btn wh-btn-primary"
            style={{ padding: "6px 10px", fontSize: 12, height: 32, background: COLORS.tertiary }}
            onClick={(e) => { e.stopPropagation(); onContinueDraft(hunt); }}
          >
            {t('list.continue')}
          </button>
        ) : rsvp === "pending" ? (
          <button
            className="wh-btn wh-btn-primary"
            style={{ padding: "6px 10px", fontSize: 12, height: 32 }}
            onClick={(e) => { e.stopPropagation(); onOpenInvite(hunt); }}
          >
            {t('list.rsvp')}
          </button>
        ) : rsvp ? (
          <button
            className="wh-btn"
            style={{ padding: "6px 10px", fontSize: 12, height: 32, background: "transparent", border: `1px solid `, color: COLORS.onSurface }}
            onClick={(e) => { e.stopPropagation(); onOpenInvite(hunt); }}
          >
            <Icon name="edit" size={12} />
            {t('list.editAnswer')}
          </button>
        ) : (
          <button
            className="wh-btn"
            style={{ padding: "6px 10px", fontSize: 12, height: 32, background: "transparent", border: `1px solid `, color: COLORS.onSurface }}
            // Match the surrounding action buttons (RSVP / Edit answer): stop
            // propagation so the row-level onClick at line 841 doesn't also
            // fire, then open the same hunt-detail view via onOpenInvite. Prior
            // version called only stopPropagation, leaving the button inert
            // while every other place on the row navigated correctly.
            onClick={(e) => { e.stopPropagation(); onOpenInvite(hunt); }}
          >
            {t('list.details')}
          </button>
        )}
        {/* The row's overflow menu, mirroring the mobile 3-dot menu.

            "Remove from my list" is offered wherever the host passes
            onRemoveHunt — the list does so for ended rows only, since hiding a
            planned hunt the viewer takes part in kept it off Hunts until it
            started. "Edit plan" only on a hunt that
            is still a plan the viewer may edit: `planned` groups ongoing with
            upcoming in this list, so `ongoing` has to be excluded explicitly,
            the same way the detail view gates its own header slot. A draft has
            Continue for this, and an ended hunt has nothing left to plan.

            The menu appears if EITHER action applies — gating it on removal
            alone would hide editing from a host that wired only that. */}
        {(onRemoveHunt || canEditPlanHere) && (
          <HuntRowMenu
            onRemove={onRemoveHunt ? () => onRemoveHunt(hunt) : undefined}
            onEdit={canEditPlanHere ? () => onEditHunt!(hunt) : undefined}
          />
        )}
      </div>
    </div>
  );
}

// Shared by every item in the row's overflow menu, so a second item cannot
// drift from the first.
const ROW_MENU_ITEM_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  border: "none",
  background: "transparent",
  padding: "8px 10px",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  color: COLORS.onSurface,
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "inherit",
} as const;

/**
 * The row's "More options" menu. Both actions are optional and the caller
 * decides which apply: `onEdit` only for a plan the viewer may edit, `onRemove`
 * only where the host wired removal. The menu is not rendered at all when
 * neither is given — see the call site.
 */
function HuntRowMenu({ onRemove, onEdit }: { onRemove?: () => void; onEdit?: () => void }) {
  const { t } = useTranslation('hunts');
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{ position: "relative" }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // The row is a role="button" that navigates on Enter/Space. Without
        // stopping keydown here, operating this menu by keyboard would ALSO
        // trigger the row's navigation — so pressing "Remove" would open the
        // hunt instead of the confirm. Escape closes the menu.
        if (e.key === "Escape") setOpen(false);
        e.stopPropagation();
      }}
    >
      <button
        type="button"
        className="wh-btn-icon"
        aria-label={t('remove.menuLabel')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        <Icon name="more" size={16} />
      </button>
      {open && (
        <>
          {/* Full-viewport click-away layer: closes the menu on any outside
              click without wiring a document listener. */}
          <div
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            style={{ position: "fixed", inset: 0, zIndex: 50 }}
          />
          <div
            role="menu"
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: 4,
              minWidth: 200,
              zIndex: 51,
              background: COLORS.surface,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              padding: 4,
            }}
          >
            {/* Edit first: removing a hunt from your list is the one you might
                regret, so it stays last. */}
            {onEdit && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); setOpen(false); onEdit(); }}
                style={ROW_MENU_ITEM_STYLE}
              >
                <Icon name="edit" size={14} />
                {t('list.editPlan')}
              </button>
            )}
            {onRemove && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); setOpen(false); onRemove(); }}
                style={ROW_MENU_ITEM_STYLE}
              >
                <Icon name="eye-off" size={14} />
                {t('remove.action')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
